/**
 * ID Generation Repository
 *
 * FERP CRITICAL: Centralized backend ID generation (P0 Security Blocker)
 * Domain-agnostic Snowflake ID system for FERP's 16 primitive types
 *
 * Principles:
 * - ALL IDs generated on backend (NEVER client-side)
 * - Snowflake IDs: 11-char Base62, time-sortable, collision-resistant
 * - Tenant-scoped with machine ID (ECS task ARN)
 * - Immutable audit trail at ID creation
 * - Repository pattern strict enforcement
 */

import { BaseRepository } from './BaseRepository';
import { gql } from '@apollo/client';
import { BackendId, asBackendId } from '../../types/branded-ids';

// ============================================================
// TypeScript Types (Backend GraphQL Schema Alignment)
// ============================================================

/**
 * Entity types matching FERP's 16 universal primitive workflow types
 * CRITICAL: Must match backend EntityType enum in graphql_gateway/src/forms_api/types/id_broker.rs
 *
 * ⚠️ IMPORTANT: Backend GraphQL schema uses camelCase (via #[graphql(rename_items = "camelCase")])
 * Rust enum is PascalCase, but GraphQL exposes as camelCase (workflowNode, actor, etc.)
 * GraphQL enum values are case-sensitive and must match exactly
 *
 * Consistent with FERP GraphQL naming convention (camelCase for all fields/enums)
 */
export enum EntityType {
  // Backend GraphQL EntityType enum (camelCase via #[graphql(rename_items = "camelCase")])
  workflowNode = 'workflowNode',       // Workflow node in DAG
  workflowEdge = 'workflowEdge',       // Workflow edge/transition
  actor = 'actor',                     // Actor performing operations
  schedule = 'schedule',               // Schedule for automated execution
  resource = 'resource',               // Resource allocation and management
  policy = 'policy',                   // Policy enforcement and governance
  notification = 'notification',       // Notification and alerting
  escalation = 'escalation',           // Escalation procedures
  correlation = 'correlation',         // Cross-system correlation
  trace = 'trace',                     // Audit trace and logging
  session = 'session',                 // User session management
  request = 'request',                 // API request tracking
  idempotency = 'idempotency',         // Idempotency token generation
  tenant = 'tenant',                   // Tenant isolation and management
  audit = 'audit',                     // Audit trail entries
  transaction = 'transaction',         // Transaction coordination

  // Legacy aliases for backward compatibility
  TRANSITION = 'workflowEdge',         // Alias for workflowEdge
  GUARD = 'workflowNode',              // Alias for workflowNode (guard logic in node)
  ACTION = 'workflowNode',             // Alias for workflowNode (action logic in node)
  CONTEXT = 'correlation',             // Alias for correlation (cross-system context)
}

export interface GenerateIdInput {
  entityType: EntityType;
  count?: number; // Default: 1, Max: 100 (backend enforced)
}

export interface GenerateIdResult {
  ids: BackendId[];
  format: string; // "snowflake"
  generatedAt: string; // ISO 8601 timestamp
  entityType: EntityType;
}

// ============================================================
// GraphQL Mutations
// ============================================================

const GENERATE_ID = gql`
  mutation GenerateId($input: GenerateIdInput!) {
    generateId(input: $input) {
      ids
      format
      generatedAt
      entityType
    }
  }
`;

// ============================================================
// Repository Implementation
// ============================================================

export class IdGenerationRepository extends BaseRepository<string> {
  /**
   * Generate single ID for entity type
   * REPOSITORY PATTERN: Uses backend generateId GraphQL mutation
   * TYPE SAFETY: Returns BackendId (branded type prevents arbitrary strings)
   *
   * @param entityType - FERP primitive type (e.g., WORKFLOW_DEFINITION, ACTOR)
   * @returns Backend-generated Snowflake ID (e.g., "wfl_0BR5kP3q7x")
   */
  async generateId(entityType: EntityType): Promise<BackendId> {
    const result = await this.generateIds(entityType, 1);
    const id = result.ids[0];
    if (!id) {
      throw new Error(`Backend failed to generate ID for entity type: ${entityType}`);
    }
    return id;
  }

  /**
   * Generate multiple IDs for batch operations
   * REPOSITORY PATTERN: Uses backend generateId GraphQL mutation
   * TYPE SAFETY: Returns BackendId[] (branded type prevents arbitrary strings)
   *
   * @param entityType - FERP primitive type
   * @param count - Number of IDs to generate (max 100, backend enforced)
   * @returns GenerateIdResult with BackendId array
   */
  async generateIds(entityType: EntityType, count: number = 1): Promise<GenerateIdResult> {
    try {
      // Validate count client-side (backend also validates)
      if (count < 1 || count > 100) {
        throw new Error('Count must be between 1 and 100');
      }

      const input: GenerateIdInput = {
        entityType,
        count,
      };

      const result = await this.graphQLClient.mutate<Record<string, any>>({
        mutation: GENERATE_ID,
        variables: { input },
      });

      // Check for GraphQL errors
      if (result.errors && result.errors.length > 0) {
        const errorMessages = result.errors.map(e => e.message).join(', ');
        throw new Error(`GraphQL errors: ${errorMessages}`);
      }

      if (!result.data?.generateId) {
        throw new Error('No data returned from generateId mutation - check backend GraphQL schema');
      }

      // Safe assertion: Backend-generated Snowflake IDs
      const backendResult = result.data.generateId;
      return {
        ids: backendResult.ids.map(asBackendId),
        format: backendResult.format,
        generatedAt: backendResult.generatedAt,
        entityType: backendResult.entityType,
      };
    } catch (error) {
      console.error('Failed to generate IDs:', {
        error,
        entityType,
        count,
        errorType: error?.constructor?.name,
      });
      throw new Error(`Failed to generate IDs: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Generate ID with automatic entity type detection from context
   * Convenience method for common use cases
   * TYPE SAFETY: Returns BackendId (branded type prevents arbitrary strings)
   *
   * @param entityTypeOrContext - Entity type enum or context string
   * @returns Backend-generated Snowflake ID
   */
  async generateContextualId(entityTypeOrContext: EntityType | string): Promise<BackendId> {
    const entityType = typeof entityTypeOrContext === 'string'
      ? this.inferEntityType(entityTypeOrContext)
      : entityTypeOrContext;

    return this.generateId(entityType);
  }

  /**
   * Infer EntityType from context string (for backward compatibility)
   * Maps legacy naming conventions to backend camelCase EntityType enum
   *
   * @param context - Context string (e.g., "workflow", "actor", "policy")
   * @returns Backend-compatible EntityType (camelCase)
   */
  private inferEntityType(context: string): EntityType {
    const lowerContext = context.toLowerCase();

    // Map legacy naming to backend EntityType enum (camelCase)
    const typeMap: Record<string, EntityType> = {
      // Workflow-related
      workflow_definition: EntityType.workflowNode,
      workflow: EntityType.workflowNode, // Legacy alias
      workflow_node: EntityType.workflowNode,
      node: EntityType.workflowNode,

      // Transition-related
      workflow_edge: EntityType.workflowEdge,
      edge: EntityType.workflowEdge,
      transition: EntityType.workflowEdge,

      // Actor
      actor: EntityType.actor,

      // Schedule
      schedule: EntityType.schedule,

      // Resource
      resource: EntityType.resource,

      // Policy
      policy: EntityType.policy,

      // Notification
      notification: EntityType.notification,

      // Escalation
      escalation: EntityType.escalation,
      escalation_path: EntityType.escalation,

      // Correlation
      correlation: EntityType.correlation,

      // Trace
      trace: EntityType.trace,
      audit_log: EntityType.trace, // Legacy alias

      // Session
      session: EntityType.session,

      // Request
      request: EntityType.request,

      // Idempotency
      idempotency: EntityType.idempotency,

      // Tenant
      tenant: EntityType.tenant,

      // Audit
      audit: EntityType.audit,

      // Transaction
      transaction: EntityType.transaction,
    };

    // Fallback to workflowNode if unknown type
    return typeMap[lowerContext] || EntityType.workflowNode
  }

  // ============================================================
  // BaseRepository Abstract Methods (Not Used for ID Generation)
  // ============================================================

  async findByTenantAndId(_tenantId: string, _id: string): Promise<string | null> {
    throw new Error('findByTenantAndId not applicable for IdGenerationRepository');
  }

  async findByTenant(_tenantId: string, _options?: any): Promise<string[]> {
    throw new Error('findByTenant not applicable for IdGenerationRepository');
  }
}

// ============================================================
// Singleton Instance Export
// ============================================================

export const idGenerationRepository = new IdGenerationRepository();
